/**
 * OpportunitiesPage Component
 * Main page for browsing opportunities with multiple views:
 * - Browse (card grid)
 * - Calendar (upcoming events)
 * - Funding (grants and partnerships)
 * - Map (location-based view)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Nav,
    Spinner,
    Alert,
    Form,
    InputGroup,
    Button,
} from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import { useOpportunityApi } from '@/shared/services/opportunityApi';
import useChatPageEffect from '@/shared/hooks/useChatPageEffect';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

import OpportunityCard from './OpportunityCard';
import OpportunityFilters from './OpportunityFilters';
import EventCalendar from './EventCalendar';
import FundingFinder from './FundingFinder';
import OpportunityMap from './OpportunityMap';

import '../Opportunities.css';

const TABS = [
    { key: 'browse', label: 'Browse', icon: '📋' },
    { key: 'calendar', label: 'Events', icon: '📅' },
    { key: 'funding', label: 'Funding', icon: '💰' },
    { key: 'map', label: 'Map', icon: '🗺️' },
];

export default function OpportunitiesPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const opportunityApi = useOpportunityApi();
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'the ecosystem';

    // State
    const [activeTab, setActiveTab] = useState(
        searchParams.get('view') || 'browse'
    );
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 12,
        total: 0,
        pages: 0,
    });

    // Filters
    const [filters, setFilters] = useState({
        type: searchParams.get('type') || '',
        audience: searchParams.get('audience') || '',
        region: searchParams.get('region') || '',
        search: searchParams.get('q') || '',
    });

    // Filter options from API
    const [filterOptions, setFilterOptions] = useState({
        opportunityTypes: [],
        targetAudiences: [],
        regions: [],
    });

    // Publish page context for chat widget
    useChatPageEffect(
        {
            page: 'opportunities',
            activeTab,
            searchQuery: filters.search || null,
            activeFilters: {
                type: filters.type || null,
                audience: filters.audience || null,
                region: filters.region || null,
            },
            resultCount: pagination.total,
        },
        [activeTab, filters, pagination.total]
    );

    // Load filter options
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const options = await opportunityApi.fetchTypes();
                setFilterOptions(options);
            } catch (err) {
                console.error('Failed to load filter options:', err);
            }
        };
        loadFilterOptions();
    }, [opportunityApi]);

    // Load opportunities
    const loadOpportunities = useCallback(async () => {
        if (activeTab !== 'browse') return;

        setLoading(true);
        setError(null);

        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
            };

            if (filters.type) params.type = filters.type;
            if (filters.audience) params.audience = filters.audience;
            if (filters.region) params.region = filters.region;

            let result;
            if (filters.search) {
                result = await opportunityApi.search(filters.search, params);
            } else {
                result = await opportunityApi.fetchOpportunities(params);
            }

            setOpportunities(result.opportunities || []);
            setPagination((prev) => ({
                ...prev,
                total: result.pagination?.total || 0,
                pages: result.pagination?.pages || 0,
            }));
        } catch (err) {
            console.error('Failed to load opportunities:', err);
            setError('Failed to load opportunities. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [activeTab, pagination.page, pagination.limit, filters, opportunityApi]);

    useEffect(() => {
        loadOpportunities();
    }, [loadOpportunities]);

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ view: tab });
    };

    // Handle filter change
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPagination((prev) => ({ ...prev, page: 1 }));

        // Update URL params
        const params = { view: activeTab };
        if (newFilters.type) params.type = newFilters.type;
        if (newFilters.audience) params.audience = newFilters.audience;
        if (newFilters.region) params.region = newFilters.region;
        if (newFilters.search) params.q = newFilters.search;
        setSearchParams(params);
    };

    // Handle search
    const handleSearch = (e) => {
        e.preventDefault();
        handleFilterChange(filters);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Render content based on active tab
    const renderContent = () => {
        switch (activeTab) {
            case 'calendar':
                return (
                    <EventCalendar
                        filters={filters}
                        filterOptions={filterOptions}
                    />
                );
            case 'funding':
                // FundingFinder reads its sector list from the tenant
                // taxonomy directly (Wave R), so we no longer thread
                // `filters` / `filterOptions` through: both were unused.
                return <FundingFinder />;
            case 'map':
                return (
                    <OpportunityMap
                        filters={filters}
                        filterOptions={filterOptions}
                    />
                );
            case 'browse':
            default:
                return renderBrowseView();
        }
    };

    // Render browse view with grid
    const renderBrowseView = () => {
        if (loading) {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading opportunities...</p>
                </div>
            );
        }

        if (error) {
            return (
                <Alert variant="danger" className="mt-4">
                    {error}
                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-3"
                        onClick={loadOpportunities}
                    >
                        Retry
                    </Button>
                </Alert>
            );
        }

        if (opportunities.length === 0) {
            return (
                <div className="text-center py-5">
                    <div className="display-1 mb-3">🔍</div>
                    <h4>No opportunities found</h4>
                    <p className="text-muted">
                        Try adjusting your filters or search terms.
                    </p>
                    <Button
                        variant="outline-primary"
                        onClick={() =>
                            handleFilterChange({
                                type: '',
                                audience: '',
                                region: '',
                                search: '',
                            })
                        }
                    >
                        Clear Filters
                    </Button>
                </div>
            );
        }

        return (
            <>
                <Row className="g-4">
                    {opportunities.map((opp) => (
                        <Col key={opp._id} sm={6} lg={4}>
                            <OpportunityCard opportunity={opp} />
                        </Col>
                    ))}
                </Row>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <nav>
                            <ul className="pagination">
                                <li
                                    className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() =>
                                            handlePageChange(
                                                pagination.page - 1
                                            )
                                        }
                                        disabled={pagination.page === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {[...Array(Math.min(pagination.pages, 5))].map(
                                    (_, i) => {
                                        const pageNum = i + 1;
                                        return (
                                            <li
                                                key={pageNum}
                                                className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() =>
                                                        handlePageChange(
                                                            pageNum
                                                        )
                                                    }
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    }
                                )}
                                <li
                                    className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() =>
                                            handlePageChange(
                                                pagination.page + 1
                                            )
                                        }
                                        disabled={
                                            pagination.page === pagination.pages
                                        }
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </>
        );
    };

    return (
        <>
            <SEO
                title="Opportunities"
                description={`Discover STEM opportunities across ${ecosystemName}: internships, grants, events, professional development, and more.`}
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Opportunities', path: '/opportunities' },
                ]}
            />

            <Container className="py-5">
                <h1 className="text-primary mb-2">Opportunities</h1>
                <p className="lead mb-4">
                    Discover STEM opportunities across {ecosystemName}:
                    internships, workshops, funding, events, and more.
                </p>

                {/* Tab Navigation */}
                <Nav
                    variant="tabs"
                    className="mb-4"
                    activeKey={activeTab}
                    onSelect={handleTabChange}
                >
                    {TABS.map((tab) => (
                        <Nav.Item key={tab.key}>
                            <Nav.Link
                                eventKey={tab.key}
                                className="d-flex align-items-center gap-2"
                            >
                                <span>{tab.icon}</span>
                                <span className="d-none d-sm-inline">
                                    {tab.label}
                                </span>
                            </Nav.Link>
                        </Nav.Item>
                    ))}
                </Nav>

                {/* Search Bar (for Browse view) */}
                {activeTab === 'browse' && (
                    <Form onSubmit={handleSearch} className="mb-4">
                        <InputGroup>
                            <Form.Control
                                type="text"
                                placeholder="Search opportunities..."
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters((prev) => ({
                                        ...prev,
                                        search: e.target.value,
                                    }))
                                }
                            />
                            <Button type="submit" variant="primary">
                                Search
                            </Button>
                        </InputGroup>
                    </Form>
                )}

                {/* Main Content */}
                <Row>
                    {/* Filters Sidebar (for Browse view) */}
                    {activeTab === 'browse' && (
                        <Col lg={3} className="mb-4 mb-lg-0 align-self-start">
                            <OpportunityFilters
                                filters={filters}
                                filterOptions={filterOptions}
                                onFilterChange={handleFilterChange}
                            />
                        </Col>
                    )}

                    {/* Content Area */}
                    <Col lg={activeTab === 'browse' ? 9 : 12}>
                        {/* Results Count (for Browse view) */}
                        {activeTab === 'browse' && !loading && (
                            <p className="text-muted mb-3">
                                {pagination.total} opportunit
                                {pagination.total !== 1 ? 'ies' : 'y'} found
                            </p>
                        )}

                        {renderContent()}
                    </Col>
                </Row>
            </Container>
        </>
    );
}
